import { default as React } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Paper } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  card: {
    padding: theme.spacing(2.5),
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.background.paper
        : theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
  headerIcon: {
    fontSize: '1.5rem',
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: theme.palette.text.primary,
  },
  includedList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  includedItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.02)',
    },
  },
  checkIcon: {
    fontSize: '1.25rem',
    color: theme.palette.success.main,
    marginTop: '2px',
    flexShrink: 0,
  },
  itemText: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    lineHeight: 1.6,
  },
}));

export const TerraformInfraOptions = ({
  onChange,
}: FieldExtensionComponentProps<Record<string, never>>) => {
  const classes = useStyles();

  // No options to collect - just render informational card
  React.useEffect(() => {
    onChange({});
  }, [onChange]);

  const included = [
    'Terraform 1.5+ infrastructure template',
    'GCP best practices (Cloud Run, GKE, etc.)',
    'State management with Cloud Storage backend',
    'Environment-specific configurations (dev, prod)',
    'Terraform Cloud/Atlantis integration ready',
    'CI/CD pipeline for infrastructure changes',
  ];

  return (
    <Box className={classes.root}>
      <Paper className={classes.card} elevation={1}>
        {/* Header */}
        <Box className={classes.header}>
          <span className={classes.headerIcon}>🏗️</span>
          <Typography className={classes.headerTitle}>
            What's Included
          </Typography>
        </Box>

        {/* Included Items */}
        <Box component="ul" className={classes.includedList}>
          {included.map((item, index) => (
            <Box component="li" key={index} className={classes.includedItem}>
              <CheckCircleIcon className={classes.checkIcon} />
              <Typography className={classes.itemText}>{item}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export const TerraformInfraOptionsExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TerraformInfraOptions',
    component: TerraformInfraOptions,
  }),
);
